import type { RealtimeEvent, RealtimeEventHandler } from "./types"

export class RealtimeEventRegistry {
	private handlers: Map<string, RealtimeEventHandler[]> = new Map()
	private handlerIds: Set<string> = new Set()

	/**
	 * Register an event handler
	 */
	register<T = any>(
		handler: Omit<RealtimeEventHandler<T>, "id"> & { id?: string },
	): string {
		const id = handler.id || this.generateHandlerId()

		if (this.handlerIds.has(id)) {
			// throw new Error(`Handler with id '${id}' already exists`);
		}

		const fullHandler: RealtimeEventHandler<T> = {
			...handler,
			id,
			priority: handler.priority ?? 100,
		}

		const eventHandlers = this.handlers.get(handler.eventType) || []
		eventHandlers.push(fullHandler)

		// Sort by priority (lower number = higher priority)
		eventHandlers.sort((a, b) => (a.priority || 100) - (b.priority || 100))

		this.handlers.set(handler.eventType, eventHandlers)
		this.handlerIds.add(id)

		return id
	}

	/**
	 * Unregister an event handler by id
	 */
	unregister(id: string): boolean {
		if (!this.handlerIds.has(id)) {
			return false
		}

		for (const [eventType, handlers] of this.handlers.entries()) {
			const index = handlers.findIndex((h) => h.id === id)
			if (index !== -1) {
				handlers.splice(index, 1)
				if (handlers.length === 0) {
					this.handlers.delete(eventType)
				}
				this.handlerIds.delete(id)
				return true
			}
		}

		return false
	}

	/**
	 * Unregister all handlers for a specific component
	 */
	unregisterByComponent(componentId: string): number {
		let count = 0
		const toRemove: string[] = []

		for (const [_eventType, handlers] of this.handlers.entries()) {
			for (let i = handlers.length - 1; i >= 0; i--) {
				const handler = handlers[i]
				if (handler.componentId === componentId) {
					toRemove.push(handler.id)
				}
			}
		}

		toRemove.forEach((id) => {
			if (this.unregister(id)) {
				count++
			}
		})

		return count
	}

	/**
	 * Get handlers for a specific event type
	 */
	getHandlers(eventType: string): RealtimeEventHandler[] {
		return [...(this.handlers.get(eventType) || [])]
	}

	/**
	 * Execute handlers for an event
	 */
	async executeHandlers<T = any>(event: RealtimeEvent<T>): Promise<void> {
		const handlers = this.getHandlers(event.event)
		const toRemove: string[] = []

		for (const handler of handlers) {
			try {
				// Check condition if exists
				if (handler.condition && !handler.condition(event.payload, event)) {
					continue
				}

				// Execute handler
				await handler.handler(event.payload, event)

				// Mark for removal if it's a one-time handler
				if (handler.once) {
					toRemove.push(handler.id)
				}
			} catch (error) {
				console.error(
					`Error executing Realtime handler ${handler.id} for event ${event.event}:`,
					error,
				)
			}
		}

		// Remove one-time handlers
		toRemove.forEach((id) => {
			this.unregister(id)
		})
	}

	/**
	 * Get all registered handlers
	 */
	getAllHandlers(): Map<string, RealtimeEventHandler[]> {
		return new Map(this.handlers)
	}

	/**
	 * Clear all handlers
	 */
	clear(): void {
		this.handlers.clear()
		this.handlerIds.clear()
	}

	/**
	 * Generate a unique handler ID
	 */
	private generateHandlerId(): string {
		return `realtime-handler-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
	}

	/**
	 * Get handler count for an event type
	 */
	getHandlerCount(eventType?: string): number {
		if (eventType) {
			return this.handlers.get(eventType)?.length || 0
		}
		return Array.from(this.handlers.values()).reduce(
			(sum, handlers) => sum + handlers.length,
			0,
		)
	}
}
